import { useState } from "react";
import {
  Button,
  Card,
  Chip,
  FieldError,
  Input,
  Label,
  Modal,
  Separator,
  Skeleton,
  TextField,
  Toast,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useForm } from "react-hook-form";

import {
  PriceAdjustment,
  priceErrorDescription,
  useAddPriceAdjustment,
  useDeletePriceAdjustment,
  useOrderPrice,
} from "../use-order-price";
import { useRefundOrder } from "../use-order-refund";

import { dt } from "@/shared/helper/datetime";

function tenge(amount: number) {
  return `${amount.toLocaleString("ru-RU")} ₸`;
}

type AdjustmentForm = {
  amount: string;
  reason: string;
};

function AddAdjustmentModal({
  orderId,
  isOpen,
  onOpenChange,
}: {
  orderId: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { mutateAsync, isPending } = useAddPriceAdjustment({ orderId });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AdjustmentForm>();

  const onSubmit = handleSubmit((form) => {
    mutateAsync({ amount: Number(form.amount), reason: form.reason.trim() })
      .then(() => {
        Toast.toast.success("Корректировка добавлена");
        reset();
        onOpenChange(false);
      })
      .catch(async (error) => {
        Toast.toast.danger(await priceErrorDescription(error));
      });
  });

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Backdrop isDismissable>
        <Modal.Container placement="center">
          <Modal.Dialog>
            <Modal.Header>
              <Modal.Heading>Корректировка цены</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="flex flex-col gap-4 py-4">
              <p className="text-xs text-neutral-400">
                Положительная сумма — надбавка, отрицательная — скидка.
                Например: −500.
              </p>

              <TextField isInvalid={!!errors.amount?.message}>
                <Label>Сумма, ₸</Label>
                <Input
                  step="any"
                  type="number"
                  {...register("amount", {
                    required: "Укажите сумму",
                    validate: (value) =>
                      Number(value) !== 0 || "Сумма не может быть нулевой",
                  })}
                />
                <FieldError>{errors.amount?.message}</FieldError>
              </TextField>

              <TextField isInvalid={!!errors.reason?.message}>
                <Label>Причина</Label>
                <Input
                  {...register("reason", {
                    required: "Укажите причину",
                    validate: (value) =>
                      value.trim().length > 0 || "Укажите причину",
                  })}
                />
                <FieldError>{errors.reason?.message}</FieldError>
              </TextField>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="ghost" onPress={() => onOpenChange(false)}>
                Отмена
              </Button>
              <Button isPending={isPending} onPress={() => onSubmit()}>
                Сохранить
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}

type RefundForm = {
  amount: string;
};

/**
 * Возврат денег клиенту. Операция необратимая: эквайринг отдаёт деньги, а
 * касса пробивает фискальный чек возврата — отменить его нельзя. Поэтому
 * сумма подставляется, но подтверждать её админ должен осознанно.
 */
function RefundModal({
  orderId,
  paidAmount,
  isOpen,
  onOpenChange,
}: {
  orderId: number;
  paidAmount: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { mutateAsync, isPending } = useRefundOrder({ orderId });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RefundForm>({ values: { amount: String(paidAmount) } });

  const onSubmit = handleSubmit((form) => {
    mutateAsync({ amount: Number(form.amount) })
      .then((response) => {
        if (response.data?.money?.alreadyRefunded) {
          Toast.toast.success("Деньги по заказу уже были возвращены");
        } else {
          Toast.toast.success("Возврат оформлен, чек возврата в очереди");
        }

        reset();
        onOpenChange(false);
      })
      .catch(async (error) => {
        Toast.toast.danger(await priceErrorDescription(error));
      });
  });

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Backdrop isDismissable>
        <Modal.Container placement="center">
          <Modal.Dialog>
            <Modal.Header>
              <Modal.Heading>Возврат денег</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="flex flex-col gap-4 py-4">
              <p className="text-xs text-neutral-400">
                Деньги вернутся на карту клиента, и по заказу пробьётся
                фискальный чек возврата. Отменить возврат нельзя. Оплачено по
                заказу: {tenge(paidAmount)}.
              </p>

              <TextField isInvalid={!!errors.amount?.message}>
                <Label>Сумма возврата, ₸</Label>
                <Input
                  step="any"
                  type="number"
                  {...register("amount", {
                    required: "Укажите сумму",
                    validate: (value) => {
                      const amount = Number(value);

                      if (amount <= 0) return "Сумма должна быть больше нуля";

                      if (amount > paidAmount)
                        return "Больше оплаченной суммы вернуть нельзя";

                      return true;
                    },
                  })}
                />
                <FieldError>{errors.amount?.message}</FieldError>
              </TextField>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="ghost" onPress={() => onOpenChange(false)}>
                Отмена
              </Button>
              <Button
                isPending={isPending}
                variant="danger"
                onPress={() => onSubmit()}
              >
                Вернуть деньги
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}

function AdjustmentRow({
  orderId,
  adjustment,
}: {
  orderId: number;
  adjustment: PriceAdjustment;
}) {
  const { mutateAsync, isPending } = useDeletePriceAdjustment({ orderId });

  const signed =
    adjustment.type === "dec_1" ? -adjustment.price : adjustment.price;

  const onDelete = () => {
    mutateAsync({ adjustmentId: adjustment.id })
      .then(() => {
        Toast.toast.success("Корректировка отменена");
      })
      .catch(async (error) => {
        Toast.toast.danger(await priceErrorDescription(error));
      });
  };

  return (
    <div className="flex items-center justify-between py-1 border-b border-neutral-100 last:border-0">
      <div className="flex flex-col">
        <span className="text-sm">
          <span
            className={
              signed < 0 ? "text-success-600 font-medium" : "font-medium"
            }
          >
            {signed > 0 ? "+" : ""}
            {tenge(signed)}
          </span>{" "}
          <span className="text-neutral-500">— {adjustment.reason}</span>
        </span>
        <span className="text-xs text-neutral-400">
          {dt(adjustment.createdAt).format("DD.MM.YYYY HH:mm")}
        </span>
      </div>
      <Button
        isIconOnly
        isPending={isPending}
        size="sm"
        variant="ghost"
        onPress={onDelete}
      >
        <Icon
          className="size-4 text-danger-500"
          icon="solar:trash-bin-trash-outline"
        />
      </Button>
    </div>
  );
}

export function OrderPriceCard({
  orderId,
  canAdjust,
}: {
  orderId: number;
  canAdjust: boolean;
}) {
  const [isAddOpen, setAddOpen] = useState(false);
  const [isRefundOpen, setRefundOpen] = useState(false);
  const { data, isLoading, isError } = useOrderPrice({ orderId });

  const price = data?.data;

  // Возвращать нечего, пока клиент ничего не оплатил.
  const canRefund = (price?.paidAmount ?? 0) > 0;

  return (
    <Card className="lg:col-span-2">
      <Card.Header className="pb-0 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon
            className="size-4 text-neutral-400"
            icon="solar:tag-price-outline"
          />
          <span className="font-semibold text-sm">Цена заказа</span>
          {price?.hasActivePayment && (
            <Chip color="warning" size="sm" variant="soft">
              Клиент оплачивает
            </Chip>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canAdjust && (
            <Button size="sm" variant="ghost" onPress={() => setAddOpen(true)}>
              <Icon className="size-4" icon="solar:pen-outline" />
              Изменить цену
            </Button>
          )}
          {canRefund && (
            <Button
              size="sm"
              variant="danger-soft"
              onPress={() => setRefundOpen(true)}
            >
              <Icon className="size-4" icon="solar:card-recive-outline" />
              Вернуть деньги
            </Button>
          )}
        </div>
      </Card.Header>
      <Separator className="mt-2" />
      <Card.Content className="flex flex-col gap-3">
        {isError && (
          <span className="text-sm text-neutral-400">
            Не удалось загрузить цену заказа
          </span>
        )}

        {isLoading && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-4 w-full rounded" />
            <Skeleton className="h-4 w-3/4 rounded" />
            <Skeleton className="h-4 w-1/2 rounded" />
          </div>
        )}

        {price && (
          <>
            <div className="flex flex-col gap-1">
              {price.positions.map((position, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-neutral-500">{position.title}</span>
                  <span>{tenge(position.amount)}</span>
                </div>
              ))}
            </div>

            {price.adjustments.length > 0 && (
              <>
                <Separator />
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-neutral-400">
                    Корректировки
                  </span>
                  {price.adjustments.map((adjustment) => (
                    <AdjustmentRow
                      key={adjustment.id}
                      adjustment={adjustment}
                      orderId={orderId}
                    />
                  ))}
                </div>
              </>
            )}

            <Separator />

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between text-sm font-semibold">
                <span>Итого</span>
                <span>{tenge(price.totalPrice)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-500">Оплачено</span>
                <span>{tenge(price.paidAmount)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-500">К оплате</span>
                <span
                  className={price.toPay > 0 ? "text-warning-600" : undefined}
                >
                  {tenge(price.toPay)}
                </span>
              </div>
            </div>
          </>
        )}
      </Card.Content>

      <AddAdjustmentModal
        isOpen={isAddOpen}
        orderId={orderId}
        onOpenChange={setAddOpen}
      />

      <RefundModal
        isOpen={isRefundOpen}
        orderId={orderId}
        paidAmount={price?.paidAmount ?? 0}
        onOpenChange={setRefundOpen}
      />
    </Card>
  );
}
